import { Search } from '@mui/icons-material';
import { Box, Button, InputAdornment, Stack, TextField } from '@mui/material';
import { useNavigate } from 'react-router';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import type { FieldSelectState } from './use-field-select';

type FieldSelectToolbarProps = Pick<
    FieldSelectState,
    | 'fieldName'
    | 'setFieldName'
    | 'debouncedFieldName'
    | 'fieldList'
    | 'showGrowersFields'
    | 'growersQuery'
    | 'selectedUserNodeId'
    | 'setSelectedUserNodeId'
    | 'hasNoGrowers'
    | 'ownNodeQuery'
    | 'setNewFieldModalOpen'
    | 'isMobile'
>;

function FieldSelectToolbar({
    fieldName,
    setFieldName,
    debouncedFieldName,
    fieldList,
    showGrowersFields,
    growersQuery,
    selectedUserNodeId,
    setSelectedUserNodeId,
    hasNoGrowers,
    ownNodeQuery,
    setNewFieldModalOpen,
    isMobile,
}: FieldSelectToolbarProps) {
    const navigate = useNavigate();

    const handleToolbarAddField = () => {
        if (settings.app.theme === 'expertobayer') {
            setNewFieldModalOpen(true);
            return;
        }
        mpTrackEvent('Field create began', { entry_source: 'recommendation-create' });
        navigate(reverse('fields.NewField'));
    };

    const addFieldButton = (
        <Button
            variant='contained'
            color='primary'
            disabled={hasNoGrowers}
            onClick={handleToolbarAddField}
            loading={ownNodeQuery.isValidating}
            sx={{ height: '100%', flex: '0 0 auto' }}
        >
            {_('Agregar lote')}
        </Button>
    );

    const growerSelect = showGrowersFields && (
        <Box
            sx={{ minWidth: 0, flex: isMobile ? 1 : '0 1 auto', width: isMobile ? undefined : 220 }}
            className='mp-mask'
        >
            <MuiSelect
                value={selectedUserNodeId}
                onChange={(e) => {
                    const next = e.target.value;
                    setSelectedUserNodeId(next === '' || next == null ? null : Number(next));
                }}
                type='number'
                inputLabel={_('Productor')}
                options={(growersQuery.data?.results || []).map((grower) => ({
                    label: grower.isSelf ? _('Mi cuenta') : grower.name,
                    value: grower.id.toString(),
                }))}
                formControlProps={{ fullWidth: true }}
                size={isMobile ? 'small' : 'medium'}
                clearable
            />
        </Box>
    );

    return (
        <Stack
            spacing={2}
            width='100%'
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'stretch' : 'center'}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                    label={_('Buscar lote por nombre')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <Search fontSize='24px' />
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => setFieldName(e.target.value)}
                    value={fieldName}
                    fullWidth
                    variant='outlined'
                    color='secondary'
                    size={isMobile ? 'small' : 'medium'}
                    disabled={!fieldList.length && !debouncedFieldName}
                />
            </Box>
            {isMobile ? (
                <Stack direction='row' spacing={2} alignItems='center'>
                    {growerSelect}
                    {addFieldButton}
                </Stack>
            ) : (
                <>
                    {growerSelect}
                    {addFieldButton}
                </>
            )}
        </Stack>
    );
}

export default FieldSelectToolbar;
